import styles from './purchasePDFPage.module.css'
import { PurchaseOrder, Supplier } from "../../../state/types"
import { useEffect, useMemo, useState } from 'react'
import { useProductHooks, useProductLoaders } from '../../../../products'
import { FormatType, O, PDFHeader } from '../../../../../features/ui'
import { PurchaseItemPDFRow } from './types'
import { useDataTypesHooks } from '../../../../../features/data-types'
import { useEmployeeLoaders } from '../../../../administration'
import { usePurchasesHooks } from '../../../hooks/purchasesHooks'
import { useBranchLoaders } from '../../../../manager'
import { Item, PriceBreakdown, usePriceOutlineHooks } from '../../../../operations'
import { addressHelpers } from '../../../../../features/address'
import { useSupplierLoaders } from '../../../loaders/supplierLoaders'
import { toDate } from '../../../../../utils/typeUtils'
import { firstItem } from '../../../../../utils/listUtils'
import { TableName } from '../../../../../tables'
import { Delivery, useDeliveryLoaders } from '../../../../deliveries'

type Props = {
    pageIndex: number
    purchase: PurchaseOrder
    items: Item[]
    pagesAmount: number
}

export const PurchasePDFPage = (props: Props) => {
    const { pageIndex, purchase, items, pagesAmount } = props
    const isLastPage = pageIndex === pagesAmount - 1

    const [supplier, setSupplier] = useState<Supplier>()
    const [delivery, setDelivery] = useState<Delivery>()
    const [rows, setRows] = useState<PurchaseItemPDFRow[]>([])

    const { describeAddress } = addressHelpers
    const { getSettings } = usePurchasesHooks()
    const purchaseDocFooter = getSettings()?.purchaseDocFooter as O<string>
    const { formatPrice, formatDate, formatIdentifier, formatPhone } = useDataTypesHooks()
    const { getPriceOutline } = usePriceOutlineHooks()
    const { describeQuantity } = useProductHooks()

    const branchLoaders = useBranchLoaders()
    const employeeLoaders = useEmployeeLoaders()
    const productLoaders = useProductLoaders()
    const supplierLoaders = useSupplierLoaders()
    const deliveryLoaders = useDeliveryLoaders()

    useEffect(() => {
        supplierLoaders().loadSupplier(purchase.supplierId).then(setSupplier)
        deliveryLoaders().loadOperationDeliveries(purchase.id).then(deliveries => setDelivery(firstItem(deliveries)!))
    }, [purchase, items])

    const employee = useMemo(() => employeeLoaders().findEmployee(purchase.employeeId)!, [purchase.employeeId])
    const branch = useMemo(() => branchLoaders().findBranch(purchase.deliveryBranchId)!, [purchase.deliveryBranchId])
    const priceOutline = useMemo(() => getPriceOutline(TableName.PURCHASE_ORDERS, purchase), [purchase])

    const deliveryDate = useMemo(() => delivery?.date, [delivery])

    const columns = [
        { name: 'supplierCode', label: 'Código' },
        { name: 'product', label: 'Producto' },
        { name: 'unitSubtotal', label: 'P. Unitario s/IVA' },
        { name: 'unitTotal', label: 'P. Unitario' },
        { name: 'quantity', label: 'Cantidad' },
        { name: 'total', label: 'P. Total' },
    ]

    const loadRows = async () => {
        const productIds = items.map(item => item.productId)
        const { loadProducts } = productLoaders()
        const products = await loadProducts(productIds)
        return items.map(item => {
            const product = products.find(product => product.id === item.productId)!
            const itemPriceOutline = priceOutline.items.find(itemPriceOutline => itemPriceOutline.productId === product.id)!
            return {
                supplierCode: product.supplierCode,
                product: product.name,
                unitSubtotal: formatPrice(itemPriceOutline.unitSubtotal),
                unitTotal: formatPrice(itemPriceOutline.unitTotal),
                quantity: describeQuantity(item.quantity, product),
                total: formatPrice(itemPriceOutline.total)
            }
        })
    }

    useEffect(() => {
        loadRows().then(newRows => setRows(newRows))
    }, [purchase, items, priceOutline])

    return (
        <div className={styles.container}>
            <div className={styles.body}>                
                <PDFHeader />

                {pageIndex === 0 && (<>
                    <div className={styles.row}>
                        <span className={styles.title}>
                            Orden de Compra #{purchase.code}
                        </span>
                        <div className={styles.date}>
                            Fecha: {formatDate(new Date(purchase.creationDate))}
                        </div>
                    </div>

                    <div className={`${styles.row} ${styles.alignStart}`}>
                        <div className={styles.infoSection}>
                            <span>Proveedor: {supplier?.name}</span>
                            <span>Dirección: {supplier?.address || '-'}</span>
                            <span>CUIT/CUIL: {formatIdentifier(FormatType.CUIT_CUIL)}</span>
                        </div>
                        <div className={`${styles.infoSection} ${styles.rightSection}`}>
                            <span>Teléfono: {supplier?.phone ? formatPhone(supplier?.phone) : '-'}</span>
                            <span>Email: {supplier?.email || '-'}</span>
                        </div>
                    </div>

                    <div className={`${styles.row} ${styles.alignStart}`}>
                        <div className={styles.infoSection}>
                            <span>Entregar en: {describeAddress(branch.address)}</span>
                            <span>Confeccionó: {employee?.name}</span>
                        </div>
                        <div className={`${styles.infoSection} ${styles.rightSection}`}>
                            <span>Fecha de Entrega: {formatDate(toDate(deliveryDate))}</span>
                        </div>
                    </div>
                </>)}

                <div className={styles.tableRow}>
                    <table className={styles.table}>
                        <thead>
                            <tr>
                                {columns.map((column, index) => (
                                    <th key={index}>
                                        {column.label}
                                    </th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                                {rows.map((row, index) => (
                                    <tr key={index}>
                                        {columns.map((column, index) => (
                                            <td key={index}>
                                                {row[column.name as keyof PurchaseItemPDFRow]}
                                            </td>
                                        ))}
                                    </tr>
                                ))}
                        </tbody>
                    </table>
                </div>
                <div className={styles.pagination}>
                    {pageIndex + 1} / {pagesAmount}
                </div>

                {isLastPage && priceOutline && (
                    <div className={`${styles.row} ${styles.priceOutline}`}>
                        <PriceBreakdown
                            data={priceOutline}
                            discountLabel='Bonificaciones'
                        />
                    </div>
                )}

                {purchaseDocFooter && (
                    <div className={styles.footer}>
                        <div className={styles.separator} />
                        <div className={styles.row}>
                            <span>{purchaseDocFooter}</span>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}
